import { Link, NavLink, useLocation } from "react-router-dom";
import { useState } from "react";
import { Menu, X, Instagram, Facebook, Mail, Phone, MapPin, Send } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { toast } from "sonner";
import { api, BRAND, formatError } from "../lib/api";

const navLinks = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/services", label: "Services" },
  { to: "/classes", label: "Upcoming Classes" },
  { to: "/group-training", label: "Group Training" },
  { to: "/faq", label: "FAQ" },
  { to: "/contact", label: "Contact" },
];

export default function Layout({ children }) {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const isAdmin = location.pathname.startsWith("/admin");
  if (isAdmin) return <>{children}</>;

  return (
    <div className="min-h-screen flex flex-col bg-brand-cream">
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between gap-6">
          <Link to="/" className="flex items-center gap-3 shrink-0" data-testid="nav-logo">
            <img src={BRAND.logoCircle} alt="KenMarq Safety Training" className="h-12 w-12 object-contain" />
            <div className="hidden sm:block leading-tight">
              <div className="font-display font-extrabold text-brand-navy text-lg tracking-tight">KENMARQ</div>
              <div className="text-[10px] font-semibold tracking-widest text-brand-coral">SAFETY & TRAINING</div>
            </div>
          </Link>
          <nav className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                data-testid={`nav-link-${link.to.replace("/", "") || "home"}`}
                className={({ isActive }) =>
                  `px-3 py-2 text-sm font-semibold rounded-lg transition-colors ${
                    isActive ? "text-brand-teal" : "text-brand-navy hover:text-brand-teal"
                  }`
                }
                end={link.to === "/"}
              >
                {link.label}
              </NavLink>
            ))}
          </nav>
          <div className="hidden lg:flex items-center gap-3">
            <Link to="/group-training" data-testid="nav-cta-request">
              <Button variant="outline" className="border-brand-navy text-brand-navy hover:bg-brand-navy hover:text-white rounded-full font-semibold btn-lift">Request Training</Button>
            </Link>
            <Link to="/classes" data-testid="nav-cta-register">
              <Button className="bg-brand-coral hover:bg-brand-coral text-white rounded-full font-semibold btn-lift">Register Now</Button>
            </Link>
          </div>
          <button
            onClick={() => setOpen(!open)}
            className="lg:hidden p-2 rounded-lg hover:bg-slate-100"
            data-testid="mobile-menu-toggle"
            aria-label="Menu"
          >
            {open ? <X className="h-6 w-6 text-brand-navy" /> : <Menu className="h-6 w-6 text-brand-navy" />}
          </button>
        </div>
        {open && (
          <div className="lg:hidden bg-white border-t border-slate-200 px-4 py-4 space-y-2">
            {navLinks.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-${link.to.replace("/", "") || "home"}`}
                className={({ isActive }) =>
                  `block px-3 py-3 text-base font-semibold rounded-lg ${
                    isActive ? "bg-brand-teal/10 text-brand-teal" : "text-brand-navy hover:bg-slate-50"
                  }`
                }
                end={link.to === "/"}
              >
                {link.label}
              </NavLink>
            ))}
            <div className="flex gap-2 pt-3 border-t border-slate-200">
              <Link to="/classes" className="flex-1" onClick={() => setOpen(false)}>
                <Button className="w-full bg-brand-coral text-white rounded-full font-semibold">Register Now</Button>
              </Link>
            </div>
          </div>
        )}
      </header>

      <main className="flex-1">{children}</main>

      <Footer />
    </div>
  );
}

function Footer() {
  const handleNewsletter = async (e) => {
    e.preventDefault();
    const email = e.target.email.value.trim();
    if (!email) return;
    try {
      await api.post("/newsletter", { email });
      toast.success("Subscribed! Watch for class updates.");
      e.target.reset();
    } catch (err) {
      toast.error(formatError(err));
    }
  };

  return (
    <footer className="bg-brand-navy text-white mt-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 grid grid-cols-1 md:grid-cols-4 gap-10">
        <div className="md:col-span-2">
          <img src={BRAND.logoHorizontal} alt="KenMarq" className="h-14 bg-white/5 rounded-xl px-3 py-2 mb-4 max-w-xs" />
          <p className="text-white/70 max-w-md leading-relaxed">
            Practical CPR, First Aid, and safety training for individuals, businesses, and organizations across Pennsylvania and New Jersey.
          </p>
          <div className="flex gap-3 mt-6">
            <a href={BRAND.instagram} target="_blank" rel="noreferrer" className="p-2 bg-white/10 rounded-full hover:bg-brand-teal transition-colors" data-testid="footer-instagram" aria-label="Instagram">
              <Instagram className="h-5 w-5" />
            </a>
            <a href={BRAND.facebook} target="_blank" rel="noreferrer" className="p-2 bg-white/10 rounded-full hover:bg-brand-teal transition-colors" data-testid="footer-facebook" aria-label="Facebook">
              <Facebook className="h-5 w-5" />
            </a>
            <a href={`mailto:${BRAND.email}`} className="p-2 bg-white/10 rounded-full hover:bg-brand-teal transition-colors" data-testid="footer-email-icon" aria-label="Email">
              <Mail className="h-5 w-5" />
            </a>
          </div>
        </div>
        <div>
          <h4 className="font-display font-bold mb-4 text-brand-amber">Get in Touch</h4>
          <ul className="space-y-3 text-sm text-white/80">
            <li className="flex gap-2"><MapPin className="h-4 w-4 shrink-0 mt-0.5 text-brand-teal" /> {BRAND.address}</li>
            <li className="flex gap-2"><Phone className="h-4 w-4 shrink-0 mt-0.5 text-brand-teal" /> {BRAND.phone}</li>
            <li className="flex gap-2"><Mail className="h-4 w-4 shrink-0 mt-0.5 text-brand-teal" /> {BRAND.email}</li>
          </ul>
          <ul className="space-y-2 mt-6 text-sm">
            <li><Link to="/privacy" className="text-white/70 hover:text-brand-teal" data-testid="footer-privacy">Privacy</Link></li>
            <li><Link to="/terms" className="text-white/70 hover:text-brand-teal" data-testid="footer-terms">Terms & Cancellation</Link></li>
            <li><Link to="/admin/login" className="text-white/50 hover:text-brand-teal text-xs" data-testid="footer-admin-login">Admin</Link></li>
          </ul>
        </div>
        <div>
          <h4 className="font-display font-bold mb-4 text-brand-amber">Class Updates</h4>
          <p className="text-white/70 text-sm mb-3">Get notified when new classes open.</p>
          <form onSubmit={handleNewsletter} className="flex flex-col gap-2" data-testid="newsletter-form">
            <Input name="email" type="email" required placeholder="you@email.com" className="bg-white/10 border-white/20 text-white placeholder:text-white/50 rounded-lg" data-testid="newsletter-email-input" />
            <Button type="submit" className="bg-brand-teal hover:bg-brand-teal-dark text-white rounded-full font-semibold gap-2" data-testid="newsletter-submit">
              <Send className="h-4 w-4" /> Subscribe
            </Button>
          </form>
        </div>
      </div>
      <div className="border-t border-white/10 py-6 text-center text-xs text-white/50">
        © {new Date().getFullYear()} KenMarq Safety Training. All rights reserved.
      </div>
    </footer>
  );
}
